/**
 * Case conversion utilities for snake_case (Go backend) ↔ camelCase (TypeScript frontend).
 * Self-contained copy — avoids dependency on any app's utils module.
 */

export function snakeToCamel(str: string): string {
  return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase())
}

export function camelToSnake(str: string): string {
  return str.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`)
}

/** Recursively convert all keys in an object from snake_case to camelCase */
export function keysToCamel<T>(obj: unknown): T {
  if (Array.isArray(obj)) {
    return obj.map(item => keysToCamel(item)) as T
  }

  if (obj !== null && typeof obj === 'object' && !(obj instanceof Date)) {
    const result: Record<string, unknown> = {}
    for (const [key, value] of Object.entries(obj)) {
      result[snakeToCamel(key)] = keysToCamel(value)
    }
    return result as T
  }

  return obj as T
}

/**
 * Keys whose values are cross-service contracts (e.g., workflow DAG).
 * The key itself is still converted, but the value is preserved as-is.
 */
const OPAQUE_BLOB_KEYS = new Set(['workflow'])

/** Recursively convert all keys in an object from camelCase to snake_case */
export function keysToSnake<T>(obj: unknown): T {
  if (Array.isArray(obj)) {
    return obj.map(item => keysToSnake(item)) as T
  }

  if (obj !== null && typeof obj === 'object' && !(obj instanceof Date)) {
    const result: Record<string, unknown> = {}
    for (const [key, value] of Object.entries(obj)) {
      result[camelToSnake(key)] = OPAQUE_BLOB_KEYS.has(key) ? value : keysToSnake(value)
    }
    return result as T
  }

  return obj as T
}
